/**
 * Credits - Credit Management
 * View and manage account credits
 */

import React, { useState, useEffect } from 'react';
import { 
  CreditCard, DollarSign, TrendingUp, TrendingDown, Plus,
  ArrowUpRight, ArrowDownRight, Clock, Loader2
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

export default function Credits() {
  const { user } = useAuth();
  const [balance, setBalance] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addAmount, setAddAmount] = useState('');
  const [addNote, setAddNote] = useState('');
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [balanceRes, txRes, usageRes] = await Promise.all([
        api.getCreditBalance(),
        api.getCreditTransactions(),
        api.getCreditUsage()
      ]);
      
      setBalance(balanceRes.data.balance || 0);
      setTransactions(txRes.data.transactions || []);
      setUsage(usageRes.data);
    } catch (error) {
      console.error('Failed to load credit data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCredits = async () => {
    if (!addAmount || parseFloat(addAmount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    try {
      setAdding(true);
      await api.addCredits({
        amount: parseFloat(addAmount),
        note: addNote || 'Manual credit'
      });
      
      toast.success('Credits added successfully');
      setShowAddModal(false);
      setAddAmount('');
      setAddNote('');
      loadData();
    } catch (error) {
      toast.error('Failed to add credits');
    } finally {
      setAdding(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  const isAdmin = ['admin', 'super_admin'].includes(user?.role);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Credits</h1>
          <p className="text-gray-500">Manage your account credits and view usage</p>
        </div>
        {isAdmin && (
          <button
            onClick={() => setShowAddModal(true)}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Credits
          </button>
        )}
      </div>

      {/* Balance Card */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-800 rounded-xl shadow-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-primary-100 text-sm">Current Balance</p>
            <p className="text-4xl font-bold mt-1">${balance.toFixed(2)}</p>
          </div>
          <div className="p-4 bg-white/20 rounded-full">
            <CreditCard className="w-8 h-8" />
          </div>
        </div>
        
        {usage && (
          <div className="mt-6 pt-6 border-t border-white/20 grid grid-cols-2 gap-4">
            <div>
              <p className="text-primary-200 text-sm">Used (30 days)</p>
              <p className="text-xl font-semibold">${usage.total_usage?.toFixed(2) || '0.00'}</p>
            </div>
            <div>
              <p className="text-primary-200 text-sm">Added (30 days)</p>
              <p className="text-xl font-semibold">${usage.total_credits?.toFixed(2) || '0.00'}</p>
            </div>
          </div>
        )}
      </div>

      {/* Usage Chart Placeholder */}
      {usage?.daily && usage.daily.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Daily Usage (Last 30 Days)</h3>
          <div className="h-48 flex items-end gap-1">
            {usage.daily.slice(-30).map((day, idx) => {
              const maxUsage = Math.max(...usage.daily.map(d => parseFloat(d.usage) || 0));
              const height = maxUsage > 0 ? ((parseFloat(day.usage) || 0) / maxUsage) * 100 : 0;
              
              return (
                <div
                  key={idx}
                  className="flex-1 bg-primary-200 hover:bg-primary-400 transition-colors rounded-t cursor-pointer"
                  style={{ height: `${Math.max(height, 2)}%` }}
                  title={`${day.date}: $${parseFloat(day.usage || 0).toFixed(4)}`}
                />
              );
            })}
          </div>
          <div className="flex justify-between text-xs text-gray-500 mt-2">
            <span>30 days ago</span>
            <span>Today</span>
          </div>
        </div>
      )}

      {/* Transactions */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="font-semibold text-gray-900">Transaction History</h3>
        </div>
        
        {transactions.length === 0 ? (
          <div className="text-center py-12">
            <DollarSign className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No transactions yet</h3>
            <p className="text-gray-500">
              Transactions will appear here when credits are used or added
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {transactions.map((tx) => (
              <div key={tx.id} className="px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`p-2 rounded-full ${
                    tx.type === 'credit' ? 'bg-green-100' : 'bg-red-100'
                  }`}>
                    {tx.type === 'credit' ? (
                      <ArrowDownRight className="w-5 h-5 text-green-600" />
                    ) : (
                      <ArrowUpRight className="w-5 h-5 text-red-600" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{tx.description}</p>
                    <p className="text-sm text-gray-500">
                      {new Date(tx.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className={`font-semibold ${
                    tx.type === 'credit' ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {tx.type === 'credit' ? '+' : '-'}${parseFloat(tx.amount).toFixed(4)}
                  </p>
                  <p className="text-sm text-gray-500">
                    Balance: ${parseFloat(tx.balance_after).toFixed(2)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Credits Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Add Credits</h3>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Amount ($)
                </label>
                <input
                  type="number"
                  value={addAmount}
                  onChange={(e) => setAddAmount(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="10.00"
                  min="0"
                  step="0.01"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Note (optional)
                </label>
                <input
                  type="text"
                  value={addNote}
                  onChange={(e) => setAddNote(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Monthly top-up"
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowAddModal(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleAddCredits}
                disabled={adding}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                {adding ? 'Adding...' : 'Add Credits'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
